import { API_CONFIG } from '../config/api';
import type { 
  Warehouse, 
  CreateWarehouseData, 
  UpdateWarehouseData
} from '../types/warehouse';

// Get all warehouses
export const getAllWarehouses = async (): Promise<Warehouse[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/warehouses`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch warehouses:', response.status, errorText);
      throw new Error(`Failed to fetch warehouses: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching warehouses:', error);
    throw new Error('Failed to fetch warehouses');
  }
};

// Create a new warehouse
export const createWarehouse = async (warehouseData: CreateWarehouseData): Promise<Warehouse> => {
  try {
    console.log('Creating warehouse with data:', warehouseData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/warehouses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(warehouseData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Warehouse creation failed:', response.status, errorText);
      
      let errorMessage = 'Failed to create warehouse';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Warehouse created successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating warehouse:', error);
    throw error;
  }
};

// Update a warehouse
export const updateWarehouse = async (warehouseData: UpdateWarehouseData): Promise<Warehouse> => {
  try {
    console.log('Updating warehouse with data:', warehouseData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/warehouses/${warehouseData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(warehouseData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Warehouse update failed:', response.status, errorText);
      
      let errorMessage = 'Failed to update warehouse';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Warehouse updated successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating warehouse:', error);
    throw error;
  }
};

// Delete a warehouse
export const deleteWarehouse = async (id: number): Promise<void> => {
  try {
    console.log('Deleting warehouse with ID:', id);

    const response = await fetch(`${API_CONFIG.BASE_URL}/warehouses/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Warehouse deletion failed:', response.status, errorText);
      
      let errorMessage = 'Failed to delete warehouse';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Warehouse deleted successfully');
  } catch (error) {
    console.error('Error deleting warehouse:', error);
    throw error;
  }
};

// Get warehouse by ID
export const getWarehouseById = async (id: number): Promise<Warehouse> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/warehouses/${id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch warehouse:', response.status, errorText);
      throw new Error(`Failed to fetch warehouse: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching warehouse:', error);
    throw new Error('Failed to fetch warehouse');
  }
};

export const getWarehouseStockWithBatches = async (warehouseId: number) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/inventory/warehouse-stock-with-batches?warehouse_id=${warehouseId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('401');
      }
      if (response.status === 403) {
        throw new Error('403');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching warehouse stock with batches:', error);
    throw error;
  }
};

